import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Edit, Trash2, X, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RECORDING_BADGES = {
  started: { label: "Recording started", cls: "bg-[#EF4444]/20 text-[#EF4444]", dot: "bg-[#EF4444] animate-pulse" },
  stopped: { label: "Recording stopped", cls: "bg-[#A1A1AA]/15 text-[#A1A1AA]", dot: "bg-[#A1A1AA]" },
  none: { label: "Not recorded", cls: "bg-[#52525B]/15 text-[#71717A]", dot: "bg-[#52525B]" },
};

const formatDuration = (sec) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

const RecordingBadge = ({ info, callId }) => {
  const b = RECORDING_BADGES[info?.status] || RECORDING_BADGES.none;
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${b.cls}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${b.dot}`} />
        {b.label}
      </span>
      {info && (
        <span className="font-mono text-xs text-[#A1A1AA]" data-testid={`recording-duration-${callId}`}>
          {formatDuration(info.seconds)}
        </span>
      )}
    </div>
  );
};

const AdminConsole = () => {
  const [calls, setCalls] = useState([]);
  const [recordingStatus, setRecordingStatus] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingCall, setEditingCall] = useState(null);
  const [formData, setFormData] = useState({
    calldatetime: "",
    ticker: "",
    completed: false
  });

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const res = await axios.get(`${API}/recording-status`);
        setRecordingStatus(res.data);
      } catch (error) {
        console.error("Error fetching recording status:", error);
      }
    };
    loadStatus();
    const id = setInterval(loadStatus, 5000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    fetchCalls();
  }, []);

  const fetchCalls = async () => {
    try {
      const response = await axios.get(`${API}/callmaster`);
      setCalls(response.data);
    } catch (error) {
      console.error("Error fetching calls:", error);
      toast.error("Failed to fetch calls");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCall) {
        await axios.put(`${API}/callmaster/${editingCall.id}`, formData);
        toast.success("Call updated successfully");
      } else {
        await axios.post(`${API}/callmaster`, formData);
        toast.success("Call created successfully");
      }
      
      setShowForm(false);
      setEditingCall(null);
      setFormData({ calldatetime: "", ticker: "", completed: false });
      fetchCalls();
    } catch (error) {
      console.error("Error saving call:", error);
      toast.error("Failed to save call");
    }
  };

  const handleEdit = (call) => {
    setEditingCall(call);
    setFormData({
      calldatetime: new Date(call.calldatetime).toISOString().slice(0, 16),
      ticker: call.ticker,
      completed: call.completed
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this call?")) return;
    
    try {
      await axios.delete(`${API}/callmaster/${id}`);
      toast.success("Call deleted successfully");
      fetchCalls();
    } catch (error) {
      console.error("Error deleting call:", error);
      toast.error("Failed to delete call");
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCall(null);
    setFormData({ calldatetime: "", ticker: "", completed: false });
  };

  return (
    <div className="min-h-screen bg-deepspace-bg font-dmsans p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-4xl font-bold font-outfit text-[#EDEDED]">
            Admin Console - Call Master
          </h1>
          {!showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="bg-volt-blue hover:bg-volt-blue/80 text-white"
              data-testid="add-call-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Call
            </Button>
          )}
        </div>

        {showForm && (
          <Card className="bg-deepspace-surface border-deepspace-border p-6 mb-6" data-testid="call-form">
            <h2 className="text-2xl font-semibold font-outfit text-[#EDEDED] mb-4">
              {editingCall ? "Edit Call" : "Add New Call"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                  Call Date & Time
                </label>
                <Input
                  type="datetime-local"
                  value={formData.calldatetime}
                  onChange={(e) => setFormData({ ...formData, calldatetime: e.target.value })}
                  required
                  className="bg-deepspace-bg border-deepspace-border text-[#EDEDED]"
                  data-testid="calldatetime-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                  Ticker
                </label>
                <Input
                  type="text"
                  value={formData.ticker}
                  onChange={(e) => setFormData({ ...formData, ticker: e.target.value })}
                  placeholder="Enter ticker symbol"
                  required
                  maxLength={50}
                  className="bg-deepspace-bg border-deepspace-border text-[#EDEDED]"
                  data-testid="ticker-input"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="completed"
                  checked={formData.completed}
                  onChange={(e) => setFormData({ ...formData, completed: e.target.checked })}
                  className="w-4 h-4 text-volt-blue bg-deepspace-bg border-deepspace-border rounded focus:ring-volt-blue"
                  data-testid="completed-checkbox"
                />
                <label htmlFor="completed" className="ml-2 text-sm text-[#A1A1AA]">
                  Completed
                </label>
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  className="bg-volt-blue hover:bg-volt-blue/80 text-white"
                  data-testid="submit-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {editingCall ? "Update" : "Create"}
                </Button>
                <Button
                  type="button"
                  onClick={handleCancel}
                  className="bg-[#52525B] hover:bg-[#52525B]/80 text-white"
                  data-testid="cancel-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}

        <Card className="bg-deepspace-surface border-deepspace-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-deepspace-bg border-b border-deepspace-border">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#EDEDED]">
                    Date & Time
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#EDEDED]">
                    Ticker
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#EDEDED]">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#EDEDED]">
                    Recording
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-[#EDEDED]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-deepspace-border">
                {calls.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-[#52525B]">
                      No calls found. Add your first call.
                    </td>
                  </tr>
                ) : (
                  calls.map((call) => (
                    <tr key={call.id} className="hover:bg-deepspace-bg transition-colors" data-testid={`call-row-${call.id}`}>
                      <td className="px-6 py-4 text-sm text-[#EDEDED]">
                        {new Date(call.calldatetime).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-volt-blue">
                        {call.ticker}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {call.completed ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#10B981]/20 text-[#10B981]">
                            Completed
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#EF4444]/20 text-[#EF4444]">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm" data-testid={`recording-status-${call.id}`}>
                        <RecordingBadge info={recordingStatus[call.id]} callId={call.id} />
                      </td>
                      <td className="px-6 py-4 text-right text-sm">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleEdit(call)}
                            className="bg-volt-blue hover:bg-volt-blue/80 text-white"
                            data-testid={`edit-btn-${call.id}`}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleDelete(call.id)}
                            className="bg-recording-red hover:bg-recording-red/80 text-white"
                            data-testid={`delete-btn-${call.id}`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminConsole;
